import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { BillingProvider } from "@/context/BillingContext";
import { LeadsProvider } from "@/context/LeadsContext";
import { FinancePinProvider } from "@/context/FinancePinContext";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import UpgradeModal from "@/components/UpgradeModal";
import FinancePinModal from "@/components/FinancePinModal";

import LandingPage from "@/pages/Landing";
import LoginPage from "@/pages/Login";
import RegisterPage from "@/pages/Register";
import VerifyEmailPage from "@/pages/VerifyEmail";
import ForgotPasswordPage from "@/pages/ForgotPassword";
import ResetPasswordPage from "@/pages/ResetPassword";
import OnboardingPage from "@/pages/Onboarding";
import AppLayout from "@/layouts/AppLayout";
import DashboardPage from "@/pages/Dashboard";
import ClientsPage from "@/pages/Clients";
import ClientDetailPage from "@/pages/ClientDetail";
import Agenda from "@/pages/Agenda";
import EventsPage from "@/pages/Events";
import TeamPage from "@/pages/Team";
import SettingsPage from "@/pages/Settings";
import QuoteStudio from "@/pages/QuoteStudio";
import QuoteStudioPreview from "@/pages/QuoteStudioPreview";
import PlansPage from "@/pages/Plans";
import AIManager from "@/pages/AIManager";
import AdminPage from "@/pages/Admin";
import PublicTimeline from "@/pages/PublicTimeline";
import PublicClient from "@/pages/PublicClient";
import PublicTrial from "@/pages/PublicTrial";
import RequestQuote from "@/pages/RequestQuote";
import { PrivacyPage, CookiePage, TermsPage } from "@/pages/Legal";
import Changelog from "@/pages/Changelog";
import Guide from "@/pages/Guide";
import PrivacyAccount from "@/pages/PrivacyAccount";
import CookieBanner from "@/components/CookieBanner";
import { PUBLIC_ROUTES } from "@/lib/publicRoutes";

// Dev-only sanity check: every <Route> declared below as a public path
// MUST be present in PUBLIC_ROUTES (and vice versa). This gives a noisy
// console warning the moment somebody adds a route in one place and
// forgets the other — preventing the class of "anonymous visitor gets
// bounced to /login" bugs we hit on /request-quote/.
const ROUTE_PATHS_DECLARED_PUBLIC = [
  "/",
  "/login",
  "/register",
  "/verify-email",
  "/forgot-password",
  "/reset-password",
  "/timeline/share/:token",
  "/cliente/:token",
  "/trial/:token",
  "/request-quote/:slug",
  "/privacy",
  "/cookie",
  "/termini",
  "/aggiornamenti",
  "/guida",
];
if (process.env.NODE_ENV !== "production") {
  // Normalize "/foo/:slug" → "/foo/" for comparison against PUBLIC_ROUTES
  const normalize = (p) => {
    const i = p.indexOf("/:");
    return i === -1 ? p : p.slice(0, i + 1);
  };
  const declared = new Set(ROUTE_PATHS_DECLARED_PUBLIC.map(normalize));
  const sourceOfTruth = new Set(PUBLIC_ROUTES.map((r) => r.path));
  for (const p of declared) {
    if (!sourceOfTruth.has(p)) {
      // eslint-disable-next-line no-console
      console.warn(`[publicRoutes] App.js declares "${p}" as public but it's missing from PUBLIC_ROUTES. Add it to /src/lib/publicRoutes.js.`);
    }
  }
  for (const p of sourceOfTruth) {
    if (!declared.has(p)) {
      // eslint-disable-next-line no-console
      console.warn(`[publicRoutes] PUBLIC_ROUTES contains "${p}" but no <Route> in App.js declares it.`);
    }
  }
}

export default function App() {
  return (
    <AuthProvider>
      <BillingProvider>
        <LeadsProvider>
        <FinancePinProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/timeline/share/:token" element={<PublicTimeline />} />
            <Route path="/cliente/:token" element={<PublicClient />} />
            <Route path="/trial/:token" element={<PublicTrial />} />
            <Route path="/request-quote/:slug" element={<RequestQuote />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/cookie" element={<CookiePage />} />
            <Route path="/termini" element={<TermsPage />} />
            <Route path="/aggiornamenti" element={<Changelog />} />
            <Route path="/guida" element={<Guide />} />
            <Route
              path="/quote-studio/preview"
              element={
                <ProtectedRoute>
                  <QuoteStudioPreview />
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute requireOnboarded={false}>
                  <OnboardingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="clients" element={<ClientsPage />} />
              <Route path="clients/:id" element={<ClientDetailPage />} />
              <Route path="events" element={<EventsPage />} />
              <Route path="agenda" element={<Agenda />} />
              <Route path="team" element={<TeamPage />} />
              <Route path="ai-manager" element={<AIManager />} />
              <Route path="plans" element={<PlansPage />} />
              <Route path="admin" element={<AdminPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="settings/quote-studio" element={<QuoteStudio />} />
              <Route path="privacy" element={<PrivacyAccount />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <UpgradeModal />
          <FinancePinModal />
          <CookieBanner />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        </FinancePinProvider>
        </LeadsProvider>
      </BillingProvider>
    </AuthProvider>
  );
}
